import { castArray } from 'lodash-es';
import { find, isEmpty, merge, omit, pathOr, pipe } from 'remeda';
import invariant from 'tiny-invariant';

import { DEFAULT_EMBED_DATA } from '@@editor/constants';
import { Element } from '@@editor/helpers';
import {
    ELEMENT_TYPES,
    type EmbedData,
    type EmbeddedContentElement,
    type EmbedElement,
    type EmbedType,
    type ImageElement,
    MIME_TYPES,
    type MimeType,
} from '@@editor/helpers/Element';
import Plain from '@@editor/serialization/Plain';
import transformValues from '@@editor/serialization/transformValues';
import {
    type EmbedContentAttributes,
    isUnityEmbed,
    type UnityEmbed,
    type UnityImage,
    type UnityParagraph,
} from '@@editor/typings/UnityElements';
import toParagraph from '@@editor/utils/toParagraph';

import { type ElementRule, type Next } from '../types';
import wrapSerializedState from '../wrapSerializedState';
import { CURRENT_VERSION } from './../constants';
import {
    createElement,
    createElementFromSingleLineState,
    createElementFromString,
} from './../deserializeNodes';
import deserializeCaption from './utils/deserializeCaption';
import serializeCaption from './utils/serializeCaption';

type ElementsWithEmbedCaption = EmbeddedContentElement | ImageElement;

const serializeEmbedCaption =
    (next: Next<UnityParagraph>) => (children: EmbedElement['children']) =>
        pipe(
            children,
            find(Element.isEmbedCaptionElement),
            toParagraph,
            next,
            castArray,
            wrapSerializedState,
        );

const serializeEmbedCredit = () => (children: EmbedElement['children']) =>
    pipe(
        children,
        find(Element.isEmbedCreditElement),
        pathOr(['children'] as const, []),
        Plain.serialize,
    );

/* Our frontend UI is related to the "type", since we use the same UI for video and livestream,
 * we unify the "type" in the frontend, even though it is a different type in the backend
 */
const adjustType = (type: EmbedType, mimetype: MimeType | undefined): EmbedType => {
    if (type === ELEMENT_TYPES.VIDEOCMS && mimetype === MIME_TYPES.VIDEOCMS_LIVESTREAM) {
        return ELEMENT_TYPES.VIDEOCMS_LIVESTREAM;
    }

    if (type === ELEMENT_TYPES.VIDEOCMS_LIVESTREAM && mimetype === MIME_TYPES.VIDEOCMS_LIVESTREAM) {
        return ELEMENT_TYPES.VIDEOCMS;
    }

    return type;
};

const isInlineEdited = (type: EmbedType): boolean =>
    type === ELEMENT_TYPES.EMBEDDED_CONTENT || type === ELEMENT_TYPES.IMAGE;

const deserializeContentAttributes = (
    type: EmbedType,
    contentAttributes: EmbedContentAttributes | undefined,
    next,
): { embed: EmbedData; children: EmbedElement['children'] } => {
    const transformedValues = transformValues(contentAttributes, [
        [['caption', 'previewImage.caption'], deserializeCaption(next)],
    ]);

    if (isInlineEdited(type)) {
        const { caption, credit, ...embed } = transformedValues;

        return {
            embed,
            children: [
                createElementFromSingleLineState(ELEMENT_TYPES.EMBED_CAPTION)(caption),
                createElementFromString(ELEMENT_TYPES.EMBED_CREDIT)(credit),
            ] satisfies ElementsWithEmbedCaption['children'],
        };
    }

    return { embed: transformedValues, children: [{ text: '' }] };
};

export const serializeContentAttributes = (
    type: EmbedType,
    embedData: EmbedData | undefined,
    children: EmbedElement['children'],
    next,
): EmbedContentAttributes => {
    const caption = isInlineEdited(type)
        ? serializeEmbedCaption(next)(children)
        : embedData?.caption && serializeCaption(next)(embedData?.caption);

    const credit = isInlineEdited(type)
        ? serializeEmbedCredit()(children)
        : (embedData?.credit ?? undefined);

    return merge(embedData, {
        ...(caption != null && { caption }),
        ...(credit != null && { credit }),
        ...(embedData?.previewImage && {
            previewImage: {
                ...embedData.previewImage,
                caption: serializeCaption(next)(embedData.previewImage.caption),
            },
        }),
    });
};

const deserialize = (
    type: EmbedType,
    partialElement,
    next,
): { data: EmbedElement['data']; children: EmbedElement['children'] } => {
    switch (type) {
        case ELEMENT_TYPES.CS_SLIDESHOW:
            return {
                data: transformValues(partialElement, [
                    ['slides', (slides: UnityImage[]): ImageElement[] => slides.map(next)],
                ]),
                children: [{ text: '' }],
            };

        default: {
            const { contentAttributes, ...rest } = partialElement;
            const { embed, children } = deserializeContentAttributes(type, contentAttributes, next);

            return {
                data: {
                    ...DEFAULT_EMBED_DATA,
                    ...rest,
                    ...(!isEmpty(embed) && { embed }),
                },
                children,
            };
        }
    }
};

const serialize = (
    type: EmbedType,
    data: EmbedElement['data'],
    children: EmbedElement['children'],
    next,
): Partial<UnityEmbed> => {
    switch (type) {
        case ELEMENT_TYPES.CS_SLIDESHOW:
            return transformValues(data, [
                ['slides', (slides: ImageElement[]): UnityImage[] => slides.map(next)],
            ]);

        default: {
            const { mimetype, src, originalSrc, ...restData } = data;
            const contentAttributes = serializeContentAttributes(
                type,
                restData.embed,
                children,
                next,
            );

            return {
                mimetype,
                src: src ? String(src) : src,
                originalSrc: originalSrc ?? undefined,
                // we don't need the rest of the data, only the containers
                contentAttributes,
                options: restData.options,
                templateElement: restData.templateElement,
            } as Partial<UnityEmbed>;
        }
    }
};

type Rule = ElementRule<EmbedElement, UnityEmbed>;

const embedRules = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnityEmbed(element)) {
            invariant(next, 'Cannot deserialize Embed without a `next` function');

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { type, version, ...rest } = element;
            const adjustedType = adjustType(type, rest.mimetype);
            const { data, children } = deserialize(adjustedType, rest, next);

            // Once every element will have a unique id (most likely after the switch to Plate.js), we can
            // remove `loadingStatusId` globally.
            data.loadingStatusId = crypto.randomUUID();

            return createElement(adjustedType, data, children);
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isEmbedElement(node)) {
            invariant(next, 'Cannot serialize Embed without a `next` function');

            const { type, data, children } = node;
            const adjustedType = adjustType(type, data.mimetype);

            return {
                version: CURRENT_VERSION,
                type: adjustedType,
                // Once every element will have a unique id (most likely after the switch to Plate.js), we can
                // remove `loadingStatusId` globally.
                ...serialize(adjustedType, omit(data, ['loadingStatusId']), children, next),
                // We could not figure out how to narrow down the type of `data` here, so we need to cast
                // the whole thing
            } as UnityEmbed;
        }
    },
};

export default embedRules;
